import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import { UstaProfileForm } from "@/components/UstaProfileForm";
import { BoostModal } from "@/components/BoostModal";
import { BoostCountdown } from "@/components/BoostCountdown";
import { ContactUnlock } from "@/components/ContactUnlock";
import { AdminUstaControls } from "@/components/AdminUstaControls";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, MapPin, BadgeCheck, CheckCircle2, Loader2, Briefcase, Heart, Send, Pencil, Images, Rocket, Eye, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function UstaProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isFav, toggle } = useFavorites();
  const [usta, setUsta] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [boostOpen, setBoostOpen] = useState(false);
  const [boost, setBoost] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [myRating, setMyRating] = useState(0);

  const load = () => api.get(`/ustalar/${id}`).then(({ data }) => setUsta(data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  const loadBoost = () => {
    api.get("/boost/status").then(({ data }) => setBoost(data.active_boost)).catch(() => {});
    api.get("/boost/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  };
  useEffect(() => {
    if (user && usta && user.id === usta.user_id) loadBoost();
  }, [user, usta]);

  if (!usta) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>;

  const isOwner = user && user.id === usta.user_id;

  const submitRating = async (n) => {
    if (!user || !user.id) { toast.error("Değerlendirme için giriş yapın"); return; }
    try { await api.post(`/ustalar/${id}/rate`, { rating: n, comment: "" }); setMyRating(n); toast.success("Değerlendirmeniz kaydedildi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const message = () => {
    if (!user || !user.id) { toast.error("Mesaj göndermek için giriş yapın"); return; }
    if (isOwner) { toast.info("Bu sizin profiliniz"); return; }
    navigate(`/mesajlar?to=${usta.user_id}&usta=1`);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {isOwner && (
        <div className="mb-4 flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-emerald-700">
          <CheckCircle2 className="h-4 w-4" /> Bu sizin herkese açık vitrininiz — ustalar sizi böyle görüyor.
        </div>
      )}
      {user?.role === "admin" && (
        <div className="mb-4 flex justify-end" data-testid="admin-usta-controls">
          <AdminUstaControls usta={usta} onSaved={load} />
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden">
        <div className="h-32 brand-gradient" />
        <div className="px-6 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 -mt-12">
            <div className="flex items-end gap-4">
              <Avatar className="h-24 w-24 border-4 border-white">
                <AvatarImage src={usta.avatar} /><AvatarFallback className="bg-emerald-100 text-emerald-700 text-2xl">{usta.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="pb-1">
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-extrabold text-slate-900">{usta.name}</h1>
                  {usta.phone_verified && <span className="text-emerald-500" title="Telefon doğrulandı"><BadgeCheck className="h-5 w-5" /></span>}
                </div>
                <p className="text-slate-500">{usta.specialty || "Usta"}</p>
                {(usta.extra_titles || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="usta-extra-titles">
                    {usta.extra_titles.map((t) => <span key={t} className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full">{t}</span>)}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isOwner ? (
                <>
                  {boost && <BoostCountdown expiresAt={boost.expires_at} />}
                  {!boost && <Button onClick={() => setBoostOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl" data-testid="usta-boost-button"><Rocket className="h-4 w-4 mr-1.5" /> Öne Çıkar</Button>}
                  {boost && <Button onClick={() => setBoostOpen(true)} variant="outline" className="rounded-xl border-amber-300 text-amber-600" data-testid="usta-boost-button"><Rocket className="h-4 w-4 mr-1.5" /> Süreyi Uzat</Button>}
                  <Button onClick={() => setEditOpen(true)} className="brand-gradient text-white rounded-xl" data-testid="usta-edit-button"><Pencil className="h-4 w-4 mr-1.5" /> Profili Düzenle</Button>
                </>
              ) : (
                <>
                  <Button onClick={message} className="brand-gradient text-white rounded-xl" data-testid="usta-message-button"><Send className="h-4 w-4 mr-1.5" /> Mesaj Gönder</Button>
                  <Button variant="outline" onClick={() => toggle("usta", usta.user_id)} className="rounded-xl border-slate-200" data-testid="usta-fav-button">
                    <Heart className={`h-4 w-4 ${isFav("usta", usta.user_id) ? "fill-emerald-500 text-emerald-500" : "text-slate-400"}`} />
                  </Button>
                </>
              )}
            </div>
          </div>

          {!isOwner && (
            <div className="mt-5 flex items-center gap-2" data-testid="usta-contact">
              <span className="text-sm font-semibold text-slate-500">Telefon:</span>
              <ContactUnlock targetUserId={usta.user_id} compact />
            </div>
          )}
          <div className="flex flex-wrap gap-6 mt-5 text-sm">
            <div className="flex items-center gap-1.5"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> <span className="font-bold">{usta.rating}</span> <span className="text-slate-400">({usta.review_count} değerlendirme)</span></div>
            <div className="flex items-center gap-1.5 text-slate-600"><Briefcase className="h-4 w-4 text-emerald-500" /> {usta.completed_jobs} tamamlanan iş</div>
            {usta.city && <div className="flex items-center gap-1.5 text-slate-600"><MapPin className="h-4 w-4 text-emerald-500" /> {usta.city}</div>}
            {usta.phone_verified && <div className="flex items-center gap-1.5 text-emerald-600 font-medium"><CheckCircle2 className="h-4 w-4" /> Telefon doğrulandı</div>}
          </div>

          {(usta.categories || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {usta.categories.map((c) => <span key={c} className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full capitalize">{c.replace(/-/g, " ")}</span>)}
            </div>
          )}

          {(usta.service_areas || []).length > 0 && (
            <div className="mt-5"><h3 className="font-bold text-slate-900 mb-2">Hizmet Bölgeleri</h3>
              <div className="flex flex-wrap gap-2">{usta.service_areas.map((s) => <span key={s} className="text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full flex items-center gap-1"><MapPin className="h-3 w-3" /> {s}</span>)}</div>
            </div>
          )}
        </div>
      </div>

      {isOwner && (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-4 mt-6 flex items-center justify-between gap-3" data-testid="usta-boost-control">
          <div className="text-sm font-semibold text-slate-700">{boost ? "🚀 Vitrininiz şu an öne çıkarılmış" : "Vitrininizi öne çıkarın"}</div>
          <Button onClick={() => setBoostOpen(true)} className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl h-10" data-testid="usta-extend-boost">
            <Rocket className="h-4 w-4 mr-1.5" /> {boost ? "Süreyi Uzat" : "Öne Çıkar"}
          </Button>
        </div>
      )}

      {/* Boost analytics (owner only) */}
      {isOwner && analytics && (
        <div className="bg-white rounded-2xl border border-slate-200/80 p-6 mt-6" data-testid="boost-analytics">
          <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2"><Rocket className="h-5 w-5 text-amber-500" /> Öne Çıkarma Analitiği</h2>
          <p className="text-sm text-slate-500 mb-4">{analytics.active ? "Aktif öne çıkarma süresince performansınız" : analytics.has_boost ? "Son öne çıkarma döneminizden bu yana" : "Henüz öne çıkmadınız — Öne Çıkar ile vitrinde görünün."}</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
              <div className="flex items-center gap-1.5 text-emerald-700 text-sm font-semibold"><Eye className="h-4 w-4" /> Profil Görüntülenme</div>
              <div className="text-3xl font-extrabold text-slate-900 mt-1" data-testid="analytics-views-boost">{analytics.views_boost}</div>
              <div className="text-xs text-slate-400 mt-0.5">Toplam: {analytics.views_total}</div>
            </div>
            <div className="rounded-2xl bg-violet-50 border border-violet-100 p-4">
              <div className="flex items-center gap-1.5 text-violet-700 text-sm font-semibold"><MessageSquare className="h-4 w-4" /> Gelen Mesaj</div>
              <div className="text-3xl font-extrabold text-slate-900 mt-1" data-testid="analytics-msgs-boost">{analytics.messages_boost}</div>
              <div className="text-xs text-slate-400 mt-0.5">Toplam: {analytics.messages_total}</div>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6 mt-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2"><Images className="h-5 w-5 text-emerald-500" /> Portföy / Vitrin</h2>
        {(!usta.portfolio || usta.portfolio.length === 0) ? <p className="text-sm text-slate-400">Henüz portföy görseli eklenmemiş.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{usta.portfolio.map((p, i) => <img key={i} src={p.image || p} alt="" className="rounded-xl h-36 w-full object-cover" />)}</div>
        )}
      </div>

      {/* Reviews */}
      <div className="bg-white rounded-2xl border border-slate-200/80 p-6 mt-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Değerlendirmeler ({usta.reviews?.length || 0})</h2>
        {!isOwner && user?.id && (
          <div className="mb-5 pb-5 border-b border-slate-100" data-testid="rate-usta">
            <div className="text-sm font-semibold text-slate-700 mb-2">Bu ustayı puanlayın</div>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => submitRating(n)} data-testid={`rate-star-${n}`} className="transition-transform hover:scale-110">
                  <Star className={`h-7 w-7 ${n <= myRating ? "fill-amber-400 text-amber-400" : "text-slate-300 hover:text-amber-300"}`} />
                </button>
              ))}
              {myRating > 0 && <span className="text-sm text-slate-500 ml-2">{myRating}/5 verdiniz</span>}
            </div>
          </div>
        )}
        {(!usta.reviews || usta.reviews.length === 0) ? <p className="text-sm text-slate-500">Henüz değerlendirme yok.</p> : (
          <div className="space-y-4">
            {usta.reviews.map((r) => (
              <div key={r.id} className="border-b border-slate-100 pb-4 last:border-0">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-900 text-sm">{r.author_name}</span>
                  <span className="flex items-center gap-0.5">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />)}</span>
                </div>
                <p className="text-sm text-slate-600 mt-1">{r.comment}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-lg rounded-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Usta Profilini Düzenle</DialogTitle></DialogHeader>
          <UstaProfileForm initial={usta} onSaved={() => { setEditOpen(false); load(); }} />
        </DialogContent>
      </Dialog>
      <BoostModal open={boostOpen} onOpenChange={setBoostOpen} type="usta" onDone={() => { load(); loadBoost(); }} />
    </div>
  );
}
